import type { LevelData } from '@/game/level';

export type Selection =
  | { kind: 'none' }
  | { kind: 'meta' }
  | { kind: 'settings' }
  | { kind: 'terrain'; segmentId: string }
  | { kind: 'obstacle'; segmentId: string; index: number }
  | { kind: 'collectible'; index: number }
  | { kind: 'trigger'; id: string }
  | { kind: 'segment'; segmentId: string }
  | { kind: 'decoration'; segmentId: string; index: number };

export type StoreEvent = 'level-changed' | 'selection-changed';
type Listener = () => void;

export class EditorStore {
  private levelData: LevelData;
  private sel: Selection = { kind: 'none' };
  private listeners = new Map<StoreEvent, Set<Listener>>();

  constructor(initial: LevelData) {
    this.levelData = initial;
  }

  get level(): LevelData { return this.levelData; }
  get selection(): Selection { return this.sel; }

  setLevel(next: LevelData): void {
    this.levelData = next;
    this.emit('level-changed');
  }

  /** Invoke a mutator that gets a mutable reference. Emits level-changed after. */
  mutate(fn: (data: LevelData) => void): void {
    fn(this.levelData);
    this.emit('level-changed');
  }

  setSelection(sel: Selection): void {
    this.sel = sel;
    this.emit('selection-changed');
  }

  on(event: StoreEvent, fn: Listener): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(fn);
  }

  off(event: StoreEvent, fn: Listener): void {
    this.listeners.get(event)?.delete(fn);
  }

  /** Publicly trigger an event (used by toolbar's undo/redo to force a redraw). */
  notify(event: StoreEvent): void { this.emit(event); }

  private emit(event: StoreEvent): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const fn of set) fn();
  }
}
